import { Chip } from '@/global/components/Chip'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/global/components/ui/command'
import {
    inputVariants,
    linhaControleClass,
    rotuloCampoClass,
    rotuloGrandeClass,
} from '@/global/components/ui/input'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/global/components/ui/popover'
import { cn } from '@/lib/utils'
import { CheckIcon, ChevronDownIcon, Loader2Icon, PlusIcon } from 'lucide-react'
import {
    type KeyboardEvent,
    type ReactNode,
    type Ref,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react'

/** Item fixo no fim da lista, para criar uma opção que ainda não existe. */
export type CreatableOption = {
    label: string
    /** Quando definido, é atribuído ao campo na seleção (modo simples). */
    value?: string
    onClick: () => void
}

type ComboboxBaseProps<T> = {
    options: readonly T[]
    getOptionLabel?: (option: T) => string
    /** Chave React de cada opção. Use quando dois rótulos puderem ser iguais. */
    getOptionKey?: (option: T) => string
    /** Opções que não podem ser marcadas nem desmarcadas. */
    getOptionDisabled?: (option: T) => boolean
    /**
     * Substitui o conteúdo do gatilho quando há seleção. Use com `multiple`
     * para resumir muitos itens em uma linha, no lugar dos chips.
     */
    renderValue?: (selecionados: T[]) => ReactNode
    /** Substitui o conteúdo de cada linha da lista — indentação, ícone, etc. */
    renderOption?: (option: T) => ReactNode
    /**
     * Quantos chips aparecem no gatilho antes de virarem um `+N`. O campo
     * ocupa uma linha só, então o excesso é resumido em vez de quebrar linha.
     */
    limitTags?: number
    /** Ref do gatilho — o gatilho é focável, então `.focus()` funciona. */
    triggerRef?: Ref<HTMLDivElement>
    label?: ReactNode
    /** Altura do campo: `medium` 56px, `small` 48px. */
    size?: 'small' | 'medium'
    /** Texto exibido no gatilho quando nada está selecionado. */
    placeholder?: string
    noOptionsText?: string
    /** Placeholder da busca dentro do popover. */
    searchPlaceholder?: string
    /**
     * Campo de busca dentro do popover. Desligue em lista curta e fechada
     * (linhas por página, por exemplo), onde procurar custa mais que ler.
     */
    searchable?: boolean
    /** Conteúdo à direita do gatilho, antes da seta — ícone de ajuda, etc. */
    endAdornment?: ReactNode
    disabled?: boolean
    required?: boolean
    error?: boolean
    helperText?: ReactNode
    /**
     * Não reserva a linha auxiliar abaixo do campo. Padrão nos comboboxes
     * soltos — cabeçalhos, rodapé do DataGrid; o `FormFieldAutocomplete`
     * reserva, para que a mensagem de erro não empurre o formulário quando
     * aparece.
     */
    unHelperText?: boolean
    /** Chamado a cada tecla na busca — usar quando o filtro é no servidor. */
    onInputChange?: (inputValue: string) => void
    /**
     * Busca controlada. Definido, o filtro passa a ser responsabilidade de quem
     * chama (servidor) e o `cmdk` para de filtrar — filtrar o que já veio
     * esconderia resultado que o servidor mandou.
     */
    inputValue?: string
    /** Conteúdo fixo no fim do popover, abaixo da lista. */
    footer?: ReactNode
    /** Rolagem infinita: há próxima página no servidor. */
    hasMore?: boolean
    /** Pede a próxima página quando o fim da lista aparece. */
    onLoadMore?: () => void
    /** Próxima página em voo — mostra o giro no fim da lista. */
    loadingMore?: boolean
    variant?: 'filled' | 'outlined'
    creatableOption?: CreatableOption
    isOptionEqualToValue?: (option: T, value: T) => boolean
    loading?: boolean
    className?: string
    id?: string
}

export type ComboboxProps<T> = ComboboxBaseProps<T> &
    (
        | { multiple: true; value: T[]; onChange: (value: T[]) => void }
        | {
              multiple?: false
              value: T | null
              onChange: (value: T | null) => void
          }
    )

function rotuloPadrao(option: unknown): string {
    if (option === null || option === undefined) return ''
    if (typeof option === 'object')
        return String((option as { label?: unknown }).label ?? '')
    return String(option)
}

/**
 * Reduz a opção à sua chave de comparação. `id` entra na ordem porque as listas
 * do app costumam ser entidades da API: sem ele a comparação cairia no próprio
 * objeto — referência — e o valor que volta do react-hook-form nunca é a mesma
 * referência da opção, o que deixava o mesmo item ser marcado várias vezes.
 */
function chavePadrao(option: unknown): unknown {
    if (option === null || typeof option !== 'object') return option
    const objeto = option as { value?: unknown; id?: unknown; label?: unknown }
    return objeto.value ?? objeto.id ?? objeto.label ?? option
}

function comparacaoPadrao(option: unknown, value: unknown): boolean {
    return chavePadrao(option) === chavePadrao(value)
}

/**
 * Realce de campo aberto. No `filled` o `after` já é absoluto — vem da
 * variante — e só engorda o underline. No `outlined` ele não existe: criá-lo
 * aqui viraria mais um item do flex da caixa e empurraria o layout 2px ao
 * abrir, então o realce é o mesmo anel do foco.
 */
const realceAberto = {
    filled: 'after:h-0.5 after:bg-primary',
    outlined: 'border-ring ring-[3px] ring-ring/50',
} as const

/**
 * Campo de escolha com busca. Gatilho com a
 * geometria do `TextField` (rótulo dentro da caixa, linha auxiliar embaixo) e a
 * lista num popover `cmdk`.
 *
 * Use no lugar do `SelectField` quando o usuário precisa **buscar** (lista longa
 * ou vinda da API), **escolher vários** (`multiple`, com chips e `limitTags`) ou
 * **criar o que não existe** (`creatableOption`). Lista curta e fechada é
 * `SelectField`.
 *
 * Fala opções cruas — `value` é a própria opção, não uma chave — e a comparação
 * passa por `isOptionEqualToValue` (padrão: `id`, senão o rótulo).
 *
 * Não conhece react-hook-form: dentro de formulário use o
 * `FormFieldAutocomplete`, nunca `Controller` + `Combobox` na mão (D28).
 */
export function Combobox<T>({
    options,
    value,
    onChange,
    getOptionLabel = rotuloPadrao,
    getOptionKey,
    getOptionDisabled,
    renderValue,
    renderOption,
    limitTags = 2,
    triggerRef,
    multiple,
    label,
    size = 'medium',
    placeholder,
    noOptionsText = 'Nenhuma opção encontrada',
    searchPlaceholder = 'Buscar...',
    searchable = true,
    endAdornment,
    disabled,
    required,
    error,
    helperText,
    unHelperText = true,
    onInputChange,
    inputValue,
    footer,
    hasMore,
    onLoadMore,
    loadingMore,
    variant = 'filled',
    creatableOption,
    isOptionEqualToValue = comparacaoPadrao,
    loading,
    className,
    id,
}: ComboboxProps<T>) {
    const chaveDe = getOptionKey ?? getOptionLabel
    const idGerado = useId()
    const idGatilho = id ?? idGerado
    const idRotulo = `${idGatilho}-rotulo`
    const idAuxiliar = `${idGatilho}-auxiliar`
    const [aberto, setAberto] = useState(false)

    const emitir = onChange as (valor: unknown) => void

    const selecionados: T[] = multiple
        ? ((value as T[] | null | undefined) ?? [])
        : value === null || value === undefined
          ? []
          : [value as T]

    const estaSelecionado = (option: T) =>
        selecionados.some(item => isOptionEqualToValue(option, item))

    function selecionar(option: T) {
        if (!multiple) {
            emitir(option)
            setAberto(false)
            return
        }

        emitir(
            estaSelecionado(option)
                ? selecionados.filter(
                      item => !isOptionEqualToValue(option, item),
                  )
                : [...selecionados, option],
        )
    }

    function remover(option: T) {
        emitir(selecionados.filter(item => !isOptionEqualToValue(option, item)))
    }

    function criar() {
        creatableOption?.onClick()
        if (!multiple && creatableOption?.value !== undefined)
            emitir(creatableOption.value)
        setAberto(false)
    }

    function abrirComTeclado(evento: KeyboardEvent<HTMLDivElement>) {
        if (disabled) return
        if (!['Enter', ' ', 'ArrowDown'].includes(evento.key)) return
        evento.preventDefault()
        setAberto(true)
    }

    const vazio = selecionados.length === 0
    /** O gatilho é de uma linha só: o que passa de `limitTags` vira um `+N`. */
    const chipsVisiveis = selecionados.slice(0, Math.max(limitTags, 0))
    const chipsExcedentes = selecionados.length - chipsVisiveis.length
    const temRotulo = label !== undefined && label !== ''
    /**
     * O gatilho é um `div`, não um controle de formulário: não há
     * `:placeholder-shown` nem valor no DOM para o CSS ler, então o estado do
     * rótulo é decidido aqui. Ele só ocupa o lugar do valor quando não há nada
     * para mostrar na linha — sem seleção, sem placeholder e com a lista
     * fechada.
     */
    const rotuloGrande = temRotulo && vazio && !placeholder && !aberto
    /**
     * Giro só enquanto não há nada para mostrar: durante a busca no servidor a
     * lista anterior continua visível.
     */
    const carregandoVazio = !!loading && options.length === 0

    /**
     * A linha auxiliar existe quando há o que dizer ou quando o campo reserva
     * o espaço — sem a reserva a mensagem de erro empurraria o formulário.
     */
    const mostrarAuxiliar = !unHelperText || !!error || helperText !== undefined

    return (
        <div className={cn('flex w-full flex-col gap-1', className)}>
            {/*
                `modal`: dentro de um diálogo o popover é portado para o
                `body`, fora do nó do diálogo — e a trava de rolagem do diálogo
                cancela o `wheel` de tudo que está fora dela, deixando a lista
                sem rolar com o mouse. Com `modal` o popover instala a própria
                trava por cima, que libera a rolagem do próprio conteúdo. É o
                que o `Select` e o `DropdownMenu` do Radix já fazem por padrão.
            */}
            <Popover
                modal
                open={aberto && !disabled}
                onOpenChange={proximo => !disabled && setAberto(proximo)}
            >
                <PopoverTrigger asChild>
                    <div
                        ref={triggerRef}
                        id={idGatilho}
                        data-slot="combobox-trigger"
                        role="combobox"
                        tabIndex={disabled ? -1 : 0}
                        aria-labelledby={temRotulo ? idRotulo : undefined}
                        aria-describedby={
                            mostrarAuxiliar ? idAuxiliar : undefined
                        }
                        aria-invalid={error || undefined}
                        aria-required={required || undefined}
                        aria-disabled={disabled || undefined}
                        data-invalid={error || undefined}
                        data-disabled={disabled || undefined}
                        onKeyDown={abrirComTeclado}
                        className={cn(
                            inputVariants({
                                variant,
                                size,
                                comRotulo: temRotulo,
                            }),
                            'cursor-pointer',
                            aberto && realceAberto[variant],
                        )}
                    >
                        {temRotulo && (
                            <span
                                id={idRotulo}
                                className={cn(
                                    rotuloCampoClass,
                                    rotuloGrande && rotuloGrandeClass,
                                )}
                            >
                                {label}
                                {required && (
                                    <span aria-hidden="true">{' *'}</span>
                                )}
                            </span>
                        )}
                        <div className={cn(linhaControleClass)}>
                            <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden">
                                {vazio && (
                                    <span className="min-w-0 truncate text-muted-foreground">
                                        {placeholder}
                                    </span>
                                )}
                                {!vazio && renderValue && (
                                    <span className="min-w-0 flex-1 truncate">
                                        {renderValue(selecionados)}
                                    </span>
                                )}
                                {!vazio &&
                                    !renderValue &&
                                    multiple &&
                                    chipsVisiveis.map(item => (
                                        <Chip
                                            key={String(chaveDe(item))}
                                            size="small"
                                            variant="filled"
                                            className="max-w-[12rem] shrink-0"
                                            label={
                                                <span className="truncate">
                                                    {getOptionLabel(item)}
                                                </span>
                                            }
                                            onDelete={() => remover(item)}
                                        />
                                    ))}
                                {!vazio && !renderValue && !multiple && (
                                    <span className="min-w-0 truncate">
                                        {getOptionLabel(selecionados[0])}
                                    </span>
                                )}
                            </div>
                            {/*
                                Fora do container que recorta: dentro dele o
                                `+N` era o último filho e o primeiro a sumir,
                                escondendo justo o aviso de que há mais
                                selecionado do que cabe na linha.
                            */}
                            {!vazio &&
                                !renderValue &&
                                multiple &&
                                chipsExcedentes > 0 && (
                                    <Chip
                                        size="small"
                                        variant="filled"
                                        className="shrink-0"
                                        label={`+${chipsExcedentes}`}
                                    />
                                )}
                            {endAdornment && (
                                // O gatilho abre no clique: sem barrar a
                                // propagação, mexer no adorno abriria a lista.
                                <span
                                    data-slot="combobox-end-adornment"
                                    className="flex shrink-0 items-center text-muted-foreground"
                                    onPointerDown={evento =>
                                        evento.stopPropagation()
                                    }
                                    onClick={evento => evento.stopPropagation()}
                                    onKeyDown={evento =>
                                        evento.stopPropagation()
                                    }
                                >
                                    {endAdornment}
                                </span>
                            )}
                            <ChevronDownIcon
                                className={cn(
                                    'size-4 shrink-0 text-muted-foreground transition-transform',
                                    aberto && 'rotate-180',
                                )}
                            />
                        </div>
                    </div>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    sideOffset={4}
                    arrow={false}
                    className="w-(--radix-popover-trigger-width) max-w-none p-0"
                >
                    <Command shouldFilter={inputValue === undefined}>
                        {searchable && (
                            <CommandInput
                                placeholder={searchPlaceholder}
                                onValueChange={onInputChange}
                                {...(inputValue !== undefined && {
                                    value: inputValue,
                                })}
                            />
                        )}
                        <CommandList className="max-h-60">
                            {carregandoVazio && (
                                <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground select-none">
                                    <Loader2Icon className="size-4 animate-spin" />
                                    Carregando...
                                </div>
                            )}
                            {!carregandoVazio && !creatableOption && (
                                <CommandEmpty className="text-muted-foreground">
                                    {noOptionsText}
                                </CommandEmpty>
                            )}
                            {!carregandoVazio && (
                                <CommandGroup>
                                    {options.map(option => {
                                        const rotulo = getOptionLabel(option)
                                        const chave = String(chaveDe(option))
                                        return (
                                            // O `cmdk` identifica o item pelo
                                            // `value`: dois rótulos iguais
                                            // viram o mesmo item — realce e
                                            // navegação pelo teclado batem nos
                                            // dois. Por isso o `value` é a
                                            // chave e o rótulo vai em
                                            // `keywords`, que é o que o filtro
                                            // local também considera.
                                            <CommandItem
                                                key={chave}
                                                value={chave}
                                                keywords={[rotulo]}
                                                disabled={getOptionDisabled?.(
                                                    option,
                                                )}
                                                onSelect={() =>
                                                    selecionar(option)
                                                }
                                            >
                                                <span className="flex-1 truncate">
                                                    {renderOption
                                                        ? renderOption(option)
                                                        : rotulo}
                                                </span>
                                                {estaSelecionado(option) && (
                                                    <CheckIcon className="text-current" />
                                                )}
                                            </CommandItem>
                                        )
                                    })}
                                </CommandGroup>
                            )}
                            {hasMore && !carregandoVazio && (
                                <SentinelaProximaPagina
                                    onVisivel={onLoadMore}
                                    carregando={loadingMore}
                                />
                            )}
                            {creatableOption && (
                                <CommandGroup forceMount>
                                    <CommandItem
                                        forceMount
                                        value={creatableOption.label}
                                        onSelect={criar}
                                    >
                                        <PlusIcon className="size-4 text-current" />
                                        <span className="flex-1 truncate">
                                            {creatableOption.label}
                                        </span>
                                    </CommandItem>
                                </CommandGroup>
                            )}
                        </CommandList>
                        {footer && (
                            <div
                                className="border-t px-3 py-1"
                                onMouseDown={evento => evento.preventDefault()}
                            >
                                {footer}
                            </div>
                        )}
                    </Command>
                </PopoverContent>
            </Popover>
            {mostrarAuxiliar && (
                <span
                    id={idAuxiliar}
                    className={cn(
                        'px-3 text-xs',
                        error ? 'text-destructive' : 'text-muted-foreground',
                    )}
                >
                    {helperText ?? ' '}
                </span>
            )}
        </div>
    )
}

/** Pede a próxima página quando entra em tela — rolagem infinita da lista. */
function SentinelaProximaPagina({
    onVisivel,
    carregando,
}: {
    onVisivel?: () => void
    carregando?: boolean
}) {
    const alvo = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const elemento = alvo.current
        if (!elemento || !onVisivel) return

        const observador = new IntersectionObserver(([entrada]) => {
            if (entrada.isIntersecting) onVisivel()
        })

        observador.observe(elemento)
        return () => observador.disconnect()
    }, [onVisivel])

    return (
        <div
            ref={alvo}
            className="flex items-center justify-center py-3 text-xs text-muted-foreground select-none"
        >
            {carregando && (
                <Loader2Icon className="size-4 animate-spin text-primary" />
            )}
        </div>
    )
}
